import { Grid, LinearProgress } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import {
  createStyles,
  makeStyles,
  MuiThemeProvider,
  Theme,
} from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import MenuIcon from "@material-ui/icons/Menu";
import clsx from "clsx";
import { useState } from "react";
import { requestActivity } from "./auth/fetch-request";
import {
  lightOrange,
  orange,
  orangeThemeDark,
  orangeThemeLight,
} from "./theme";
import { ToggleButtonApi, ToggleMenu } from "./toggle-button";
const useStyles = (drawerWidth = 240) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        display: "flex",
        maxWidth: "100vw",
      },
      appBar: {
        zIndex: theme.zIndex.drawer + 1,
      },
      drawer: {
        width: drawerWidth,
        flexShrink: 0,
        overflow: "hidden",
      },
      drawerPaper: {
        width: drawerWidth,
        overflow: "hidden",
      },
      drawerContainer: {
        overflow: "auto",
      },
      // From persistent styles
      menuButton: {
        marginRight: theme.spacing(2),
      },
      hide: {
        display: "none",
      },
      drawerHeader: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
        justifyContent: "flex-end",
      },
      content: {
        flexGrow: 1,
        padding: theme.spacing(2),
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: -drawerWidth,
      },
      contentNoPadding: {
        flexGrow: 1,
        padding: 0,
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: -drawerWidth,
      },
      contentShift: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      },
      paddedContent: {
        padding: theme.spacing(0, 2),
      },
      drawerHeaderItem: {
        width: "100%",
      },
    })
  );

export interface PageProps {
  mainHeader?: JSX.Element | string;
  main?: JSX.Element;
  drawer?: JSX.Element;
  drawerHeader?: JSX.Element | string;
  drawerWidth?: number;
  drawerOpen?: boolean;
  loading?: boolean;
}

export const defaultPageProps: PageProps = {
  drawerWidth: 400,
};

function Loading() {
  const loading = requestActivity((s) => s.active);
  return (
    <>
      {loading && (
        <LinearProgress
          id="progress-bar"
          style={{
            backgroundColor: lightOrange,
            top: 0,
          }}
        />
      )}
    </>
  );
}

export function Page(props: PageProps) {
  const p = { ...defaultPageProps, ...props };
  const classes = useStyles(p.drawerWidth)();

  const [open, setOpen] = useState(p.drawerOpen);

  const toggleOpen = () => {
    setOpen(!open);
  };

  const mainHeader =
    typeof p.mainHeader === "string" ? (
      <Typography variant="h6">{p.mainHeader}</Typography>
    ) : (
      p.mainHeader
    );

  const drawerHeader =
    typeof p.drawerHeader === "string" ? (
      <Typography variant="h6">{p.drawerHeader}</Typography>
    ) : (
      p.drawerHeader
    );

  const hasDrawer = drawerHeader || p.drawer;
  const headerOffset = { marginTop: "45px", width: "100%" };

  return (
    <MuiThemeProvider theme={orangeThemeDark}>
      <div className={classes.root}>
        <CssBaseline />
        <AppBar position="fixed" className={classes.appBar}>
          <span>
            <Toolbar variant="dense" style={{ paddingRight: "0px" }}>
              {hasDrawer ? (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={toggleOpen}
                  edge="start"
                  className={clsx(classes.menuButton)}
                >
                  <MenuIcon id="open_menu_icon" />
                </IconButton>
              ) : null}
              {mainHeader}
            </Toolbar>
            <Loading />
          </span>
        </AppBar>
        <MuiThemeProvider theme={orangeThemeLight}>
          <CssBaseline />
          {hasDrawer ? (
            <Drawer
              className={classes.drawer}
              variant="persistent"
              anchor="left"
              open={open}
              classes={{
                paper: classes.drawerPaper,
              }}
            >
              <div className={classes.paddedContent} style={headerOffset}>
                {p.drawer}
              </div>
            </Drawer>
          ) : null}
          {hasDrawer ? (
            <main
              style={{ width: "100%" }}
              className={clsx(classes.content, {
                [classes.contentShift]: open,
              })}
            >
              <div style={headerOffset}>{p.main}</div>
            </main>
          ) : (
            <main
              className={clsx(classes.content, {
                [classes.contentShift]: true,
              })}
            >
              <div style={headerOffset}>{p.main}</div>
            </main>
          )}
        </MuiThemeProvider>
      </div>
    </MuiThemeProvider>
  );
}

export interface PageDrawerProps {
  drawer: JSX.Element;
  drawerHeader: JSX.Element | string;
  main: JSX.Element;
  drawerWidth?: number;
  drawerOpen?: boolean;
  drawerBackgroundColor?: "string";
  toggleOpenColor?: "string";
  toggleClosedColor?: "string";
  toggle?: ToggleButtonApi;
}

//TODO support all sides
export function PageDrawer(props: PageDrawerProps) {
  const p = { ...{ drawerWidth: 400, drawerOpen: true }, ...props };
  const { toggle } = p;

  const [open, setOpen] = useState(p.drawerOpen);
  const openColor = p.toggleOpenColor || "white";
  const closedColor = p.toggleClosedColor || orange;
  const backgroundColor = p.drawerBackgroundColor || orange;

  const toggleOpen = () => {
    setOpen(!open);
  };

  const drawerHeader =
    typeof p.drawerHeader === "string" ? (
      <Typography variant="h6">{p.drawerHeader}</Typography>
    ) : (
      p.drawerHeader
    );

  const toggleButtonPosition: any = {
    position: "absolute",
    top: "0px",
    left: "12px",
    zIndex: 1299,
  };
  const classes = useStyles(p.drawerWidth)();

  return (
    <>
      <CssBaseline />
      <div className={classes.root} style={{ width: "100vw" }}>
        <Drawer
          className={classes.drawer}
          variant="persistent"
          anchor="left"
          open={open}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          {toggle && (
            <div style={toggleButtonPosition}>
              <ToggleMenu
                api={toggle}
                onClick={toggleOpen}
                iconColor="white"
                active={true}
              />
            </div>
          )}
          <Grid container direction="row" style={{ width: p.drawerWidth }}>
            <Grid item style={{ width: "100%" }}>
              <Grid
                container
                direction="column"
                style={{ width: "100%", padding: "12px", backgroundColor }}
                justify="center"
                alignItems={"center"}
              >
                <Grid item style={{ color: openColor }}>
                  {drawerHeader}
                </Grid>
              </Grid>
            </Grid>
            <Grid item style={{ width: "100%" }}>
              {p.drawer}
            </Grid>
          </Grid>
        </Drawer>
        <main
          className={clsx(classes.contentNoPadding, {
            [classes.contentShift]: open,
          })}
        >
          <div>
            {toggle && (
              <div style={toggleButtonPosition}>
                <ToggleMenu api={toggle} onClick={toggleOpen} active={false} />
              </div>
            )}
            {p.main}
          </div>
        </main>
      </div>
      {!toggle && (
        <div style={toggleButtonPosition}>
          <IconButton
            aria-label="open drawer"
            onClick={toggleOpen}
            edge="start"
          >
            {open ? (
              <MenuIcon htmlColor="white" id="open_menu_icon" />
            ) : (
              <MenuIcon id="open_menu_icon" />
            )}
          </IconButton>
        </div>
      )}
    </>
  );
}
